// <pendant-scene>: the 3D studio. Pendant (extruded <lume-shape>s) + chain loop,
// HDR environment, key/rim lights, orbit-style camera rig.
import {Element, element} from '@lume/element'
import {signalify} from 'classy-solid'
import html from 'solid-js/html'
import 'lume'
import {
  Group,
  Color,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Vector3,
  MeshPhysicalMaterial,
  PMREMGenerator,
  NeutralToneMapping,
  EquirectangularReflectionMapping,
} from 'three'
import {currentTheme} from './theme.js'
import {LOOKS} from './looks.js'
import {desaturate} from './hdr-tune.js'
import {RGBELoader} from 'three/examples/jsm/loaders/RGBELoader.js'
import {METALS} from '../imports/config.js'
import {chainPath, bailPositions, necklacePoints, ringThreadPoint, linkGeometry, ringGeometry} from './chain.js'
import {BUST_UPI, bustGeometry, bustFrontZ, drapePath} from './bust.js'

const PX_PER_INCH = 22 // exaggerated so chain length is visible
const DEPTH = 9 // pendant extrusion
const PENDANT_TOP = 10 // y of the pendant's top edge (y is down)

/**
 * Lume rotation function: gentle Y sway of the pendant.
 * @param {number} x current x rotation
 * @param {number} y current y rotation
 * @param {number} z current z rotation
 * @param {number} t time in ms
 * @returns {[number, number, number]}
 */
const swayRotation = (x, y, z, t) => [x, Math.sin(t / 2500) * 14, z]

export const PendantScene = element('pendant-scene')(
  class PendantScene extends Element {
    hasShadow = false
    /** @type {import('three').Shape[]} */
    shapes = []
    /** @type {import('../types/pendant.js').MetalId} */
    metal = 'gold'
    chain = 18
    still = false
    /** show the mannequin bust and drape the chain over it */
    bust = true
    /** pendant tilt (deg about x) so it rests on the chest */
    tilt = 0
    /** @type {import('../types/pendant.js').ThemeMode} */
    mode = currentTheme()
    /** pendant height in scene units (set from the normalized shapes) */
    height = 60
    /** @type {import('three').Group | null} */
    chainGroup = null
    constructor() {
      super()
      signalify(this, 'shapes', 'metal', 'chain', 'height', 'still', 'mode', 'bust', 'tilt')
      this.height = 60
    }
    /** @returns {import('../types/pendant.js').LumeStage | null} */
    get stage() {
      return /** @type {import('../types/pendant.js').LumeStage | null} */ (this.querySelector('#stage'))
    }
    // Lume's `environment` attribute only supports LDR jpg/png, so we load the
    // real HDR (RGBE) ourselves: the raw equirect becomes scene.background and the
    // PMREM-filtered version becomes scene.environment.
    connectedCallback() {
      super.connectedCallback()
      this.onTheme = e => {
        this.mode = /** @type {CustomEvent} */ (e).detail
        this.applyLook()
      }
      document.addEventListener('themechange', this.onTheme)
      this.ro = new ResizeObserver(() => this.frame())
      this.ro.observe(this)
      this.applyLook()
      this.createEffect(() => {
        // re-run when pendant, metal, chain length or height change
        void [this.shapes, this.metal, this.chain, this.height, this.bust]
        this.buildChain()
      })
    }
    disconnectedCallback() {
      super.disconnectedCallback()
      if (this.onTheme) document.removeEventListener('themechange', this.onTheme)
      this.ro?.disconnect()
    }
    /** @type {ResizeObserver | undefined} */
    ro = undefined
    /** @type {((e: Event) => void) | undefined} */
    onTheme = undefined
    /** @type {Partial<Record<import('../types/pendant.js').ThemeMode, import('three').Texture>>} */
    bgTextures = {}
    /** @type {Partial<Record<import('../types/pendant.js').ThemeMode, import('three').Texture>>} */
    envTextures = {}
    lookSeq = 0
    /** Load (once per mode) and apply the HDR background/environment + tone mapping for the current mode. */
    applyLook() {
      const mine = ++this.lookSeq
      const mode = this.mode
      const look = LOOKS[mode]
      const stage = this.stage
      const gl = stage?.glRenderer
      if (!stage || !gl) return void requestAnimationFrame(() => this.applyLook())
      gl.toneMapping = NeutralToneMapping
      gl.toneMappingExposure = look.exposure
      /** Apply cached textures to the scene. */
      const apply = () => {
        if (mine !== this.lookSeq) return
        const scene = stage.three
        scene.background = this.bgTextures[mode] ?? null
        scene.environment = this.envTextures[mode] ?? null
        scene.environmentIntensity = look.envIntensity
        scene.backgroundIntensity = look.backgroundIntensity
        scene.backgroundBlurriness = look.blur
        scene.backgroundRotation.y = scene.environmentRotation.y = look.rotation
        stage.needsUpdate()
        this.envReady = true
        this.envMode = mode
        this.updateBustMaterial()
      }
      if (this.bgTextures[mode]) return apply()
      new RGBELoader().loadAsync(look.hdr).then(hdr => {
        desaturate(hdr, look.desaturate)
        hdr.mapping = EquirectangularReflectionMapping
        const pmrem = new PMREMGenerator(gl)
        this.envTextures[mode] = pmrem.fromEquirectangular(hdr).texture
        this.bgTextures[mode] = hdr
        pmrem.dispose()
        apply()
      })
    }
    /** @type {import('../types/pendant.js').ThemeMode | ''} */
    envMode = ''
    envReady = false
    /** Latest bail ring centres (scene coords), exposed for tests and the mannequin card. */
    bails = /** @type {{left: {x: number, y: number}, right: {x: number, y: number}} | null} */ (null)
    /** Number of chain links currently built. */
    linkCount = 0
    /** @type {import('three').Mesh | null} */
    bustMesh = null
    /** @type {import('three').MeshPhysicalMaterial | null} */
    bustMat = null
    /** Bust skin per theme: warm-white matte in light mode, lighter warm-grey matte with a sheen in dark mode. */
    updateBustMaterial() {
      const m = this.bustMat
      if (!m) return
      const dark = this.mode === 'dark'
      m.color.set(dark ? '#0a0a0c' : '#26211c')
      m.roughness = dark ? 0.8 : 0.82
      m.sheen = dark ? 0.35 : 0.3
      m.specularIntensity = dark ? 0.3 : 0.5
      m.sheenRoughness = 0.55
      m.sheenColor.set(dark ? '#5a5870' : '#e9dcc8')
      m.needsUpdate = true
    }
    /**
     * Show/hide and place the mannequin, and aim the camera at the neck-to-pendant span.
     * @param {any} host three object that holds the necklace (#body)
     * @param {number} yn world y of the necklace line on the neck
     * @param {number} zShift bust-local z to world z
     * @param {number} width pendant width in scene units (bail to bail)
     * @param {number} oy pendant centre y
     */
    placeBust(host, yn, zShift, width, oy) {
      if (!this.bustMesh) {
        this.bustMat = new MeshPhysicalMaterial({metalness: 0, roughness: 0.85, envMapIntensity: 0.2})
        this.bustMesh = new Mesh(bustGeometry(), this.bustMat)
        this.bustMesh.frustumCulled = false
        this.bustMesh.receiveShadow = true // soft shadow of chain and pendant
        this.updateBustMaterial()
      }
      const m = this.bustMesh
      if (!this.bust) {
        host.remove(m)
        return
      }
      if (m.parent !== host) host.add(m)
      m.position.set(0, -yn, zShift)
      const rig = /** @type {any} */ (this.querySelector('#rig'))
      this.frameSpec = {yn, oy, width}
      if (rig) this.frame(true)
    }
    /** @type {{yn: number, oy: number, width: number} | null} */
    frameSpec = null
    lastFrame = ''
    /**
     * Aim the camera so the view spans neck base to mid-chest (pendant is the hero) and is wide enough for the
     * pendant and the neck on narrow (mobile) screens. Re-run on resize; user zoom is kept unless the framing changed.
     * @param {boolean} [force] re-frame even if the spec is unchanged
     */
    frame(force = false) {
      const spec = this.frameSpec
      const rig = /** @type {any} */ (this.querySelector('#rig'))
      const stage = this.stage
      if (!spec || !rig || !stage || !this.bust) return
      const cam = /** @type {import('three').PerspectiveCamera} */ (stage.threeCamera)
      const w = this.clientWidth || 1
      const h = this.clientHeight || 1
      const aspect = w / h
      const top = spec.yn - 95
      const bottom = spec.oy + this.height / 2 + 75
      const needH = bottom - top
      const needW = Math.max(300, spec.width + 150)
      const tanHalf = Math.tan((cam.fov * Math.PI) / 360)
      const dist = Math.max(needH / (2 * tanHalf), needW / aspect / (2 * tanHalf)) + 40
      const key = [Math.round(needH), Math.round(needW), Math.round(aspect * 20)].join('|')
      if (!force && key === this.lastFrame) return
      this.lastFrame = key
      rig.position.y = (top + bottom) / 2
      rig.distance = dist
    }
    /** Build chain links + two bail rings from the live pendant shape (imperative three meshes). */
    buildChain() {
      const host = /** @type {any} */ (this.querySelector('#body'))?.three
      const stage = this.stage
      if (!host || !stage) {
        requestAnimationFrame(() => this.buildChain())
        return
      }
      const shapes = this.shapes
      const metal = this.mat
      const inches = this.chain
      const oy = PENDANT_TOP + this.height / 2
      if (this.chainGroup) {
        this.chainGroup.traverse(o => {
          if (o instanceof Mesh || o instanceof InstancedMesh) o.geometry.dispose()
        })
        host.remove(this.chainGroup)
      }
      const group = new Group()
      this.chainGroup = group
      host.add(group)
      if (!shapes.length) return
      const b = bailPositions(shapes)
      const left = {x: b.left.x, y: b.left.y + oy}
      const right = {x: b.right.x, y: b.right.y + oy}
      this.bails = {left, right}
      // chain and bails share the pendant's metal exactly (same colour/roughness)
      const mat = new MeshStandardMaterial({
        color: new Color(metal.color).multiplyScalar(0.3),
        metalness: 1,
        roughness: Math.max(metal.roughness, 0.3),
      })
      const ringGeo = ringGeometry()
      /** @type {import('./chain.js').V3[]} */
      let path
      /** @type {import('./chain.js').V3} */
      let reference = [0, 0, 1]
      /** @type {{x: number, y: number, z: number, a: number}[]} */
      let rings = [left, right].map(r => ({x: r.x, y: r.y, z: 0, a: 0}))
      if (this.bust) {
        // drape over the mannequin: iterate tilt / bust placement / V depth until they agree (no physics)
        let yn = oy - 120
        let alpha = 0
        let zShift = 0
        /** @type {ReturnType<typeof drapePath> | undefined} */
        let drape
        for (let it = 0; it < 4; it++) {
          // tilt follows the chest slope across the pendant's height; the pendant's back then rests on the
          // highest surface point under it (never inside the notch / collarbone ridges)
          const hh = this.height / 2 + 4
          const hw = (right.x - left.x) / 2 + 10
          const d0 = oy - yn
          alpha = Math.min(0.45, Math.max(0, Math.atan((bustFrontZ(0, d0 + hh) - bustFrontZ(0, d0 - hh)) / (2 * hh))))
          const [sa, ca] = [Math.sin(alpha), Math.cos(alpha)]
          zShift = Infinity
          for (let i = -8; i <= 8; i++)
            for (let j = -12; j <= 12; j++) {
              const dy = (j / 12) * hh
              zShift = Math.min(zShift, dy * sa - (DEPTH / 2 + 2.5) - bustFrontZ((i / 8) * hw, d0 + dy * ca))
            }
          /** @param {{x: number, y: number}} p @returns {{x: number, y: number, z: number}} */
          const tiltPt = p => ({x: p.x, y: oy + (p.y - oy) * ca, z: (p.y - oy) * sa})
          rings = [left, right].map(r => ({...tiltPt(r), a: alpha}))
          const thread = [left, right].map(r => {
            const t = ringThreadPoint(r)
            return /** @type {import('./chain.js').V3} */ ([t[0], oy + (t[1] - oy) * ca, (t[1] - oy) * sa])
          })
          drape = drapePath(thread[0], thread[1], inches * BUST_UPI, zShift)
          yn = drape.neckY
        }
        path = /** @type {NonNullable<typeof drape>} */ (drape).points
        reference = [0, -Math.sin(alpha), Math.cos(alpha)]
        this.tilt = (alpha * 180) / Math.PI
        this.placeBust(host, yn, zShift, right.x - left.x, oy)
      } else {
        this.tilt = 0
        this.placeBust(host, 0, 0, 0, oy)
        path = necklacePoints(inches, left, right, PX_PER_INCH)
      }
      for (const r of rings) {
        const m = new Mesh(ringGeo, mat)
        m.position.set(r.x, -r.y, r.z) // Lume is y-down; raw three objects are y-up
        m.rotation.x = -r.a
        group.add(m)
      }
      const links = chainPath(path, {reference})
      this.linkCount = links.length
      const inst = new InstancedMesh(linkGeometry(), mat, links.length)
      const mx = new Matrix4()
      for (const l of links) {
        // basis: X = tangent (long axis), Z = plane normal, Y = Z x X
        const [tx, ty, tz] = [l.t[0], -l.t[1], l.t[2]]
        const [nx, ny, nz] = [l.n[0], -l.n[1], l.n[2]]
        mx.makeBasis(
          new Vector3(tx, ty, tz),
          new Vector3(ny * tz - nz * ty, nz * tx - nx * tz, nx * ty - ny * tx),
          new Vector3(nx, ny, nz),
        )
        mx.setPosition(l.p[0], -l.p[1], l.p[2])
        inst.setMatrixAt(l.i, mx)
      }
      inst.frustumCulled = false
      group.traverse(o => (o.castShadow = true))
      inst.castShadow = true
      group.add(inst)
      stage.needsUpdate()
    }
    /**
     * Render one frame synchronously (drawing buffer is only readable right after render) and return a PNG data URL on a dark backdrop.
     * @param {number} [width] output width in px
     * @returns {string} PNG data URL
     */
    capturePng(width = 800) {
      const stage = /** @type {import('../types/pendant.js').LumeStage} */ (this.stage)
      const gl = stage.glRenderer
      // 1) render without the backdrop to find the necklace's alpha bounding box
      const bg = stage.three.background
      stage.three.background = null
      const bustVisible = this.bustMesh ? this.bustMesh.visible : true
      if (this.bustMesh) this.bustMesh.visible = false // frame the crop on the necklace, not the whole torso
      gl.render(stage.three, stage.threeCamera)
      const src = gl.domElement
      // crop to the visible necklace (alpha bounding box) so the preview is well framed on any screen shape
      const probe = document.createElement('canvas')
      probe.width = src.width
      probe.height = src.height
      const pctx = /** @type {CanvasRenderingContext2D} */ (probe.getContext('2d', {willReadFrequently: true}))
      pctx.drawImage(src, 0, 0)
      const {data} = pctx.getImageData(0, 0, probe.width, probe.height)
      let x0 = probe.width,
        y0 = probe.height,
        x1 = 0,
        y1 = 0
      for (let y = 0; y < probe.height; y += 2)
        for (let x = 0; x < probe.width; x += 2)
          if (data[(y * probe.width + x) * 4 + 3] > 8) {
            if (x < x0) x0 = x
            if (x > x1) x1 = x
            if (y < y0) y0 = y
            if (y > y1) y1 = y
          }
      // 2) re-render with the themed backdrop for the actual preview pixels
      stage.three.background = bg
      if (this.bustMesh) this.bustMesh.visible = bustVisible
      gl.render(stage.three, stage.threeCamera)
      pctx.clearRect(0, 0, probe.width, probe.height)
      pctx.drawImage(src, 0, 0)
      if (x1 <= x0 || y1 <= y0) [x0, y0, x1, y1] = [0, 0, probe.width, probe.height]
      const pad = 0.12
      let cw = (x1 - x0) * (1 + pad * 2),
        ch = (y1 - y0) * (1 + pad * 2)
      const aspect = 4 / 3
      if (cw / ch < aspect) cw = ch * aspect
      else ch = cw / aspect
      const cx = (x0 + x1) / 2,
        cy = (y0 + y1) / 2
      const h = Math.round(width / aspect)
      const c = document.createElement('canvas')
      c.width = width
      c.height = h
      const ctx = /** @type {CanvasRenderingContext2D} */ (c.getContext('2d'))
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(probe, cx - cw / 2, cy - ch / 2, cw, ch, 0, 0, width, h)
      return c.toDataURL('image/png')
    }
    /** @returns {import('../types/pendant.js').MetalOption} */
    get mat() {
      return METALS.find(m => m.id === this.metal) ?? METALS[1]
    }
    template = () => {
      const matProps = () => this.mat
      return html`
        <lume-scene id="stage" background-opacity="0" webgl fog-mode="none" style="width:100%;height:100%">
          <lume-camera-rig
            id="rig"
            initial-distance="520"
            min-distance="160"
            max-distance="1600"
            vertical-angle="4"
            min-vertical-angle="-50"
            max-vertical-angle="50"
            min-horizontal-angle="-70"
            max-horizontal-angle="70"
            dolly-speed="0.6"
            align-point="0.5 0.5 0.5"
            position="0 60 0"
          ></lume-camera-rig>

          <lume-element3d id="necklace" size="0 0 0" position="0 0 0" align-point="0.5 0.5 0.5">
            <lume-ambient-light intensity=${() => LOOKS[this.mode].ambient} color="#fff6ea"></lume-ambient-light>
            <!-- soft key (warm, upper left front) -->
            <lume-point-light
              position="-260 -170 330"
              intensity=${() => LOOKS[this.mode].key}
              color="#fff1dd"
              cast-shadow="true"
              shadow-map-size="1024"
              shadow-radius="6"
              shadow-bias="-0.0008"
              shadow-camera-near="60"
              shadow-camera-far="900"
            ></lume-point-light>
            <!-- cool rim from behind right -->
            <lume-point-light
              position="300 -60 -240"
              intensity=${() => LOOKS[this.mode].rim}
              color="#cfe3ff"
              cast-shadow="false"
            ></lume-point-light>
            <!-- fill from below right -->
            <lume-point-light
              position="200 220 260"
              intensity=${() => LOOKS[this.mode].fill}
              color="#ffffff"
              cast-shadow="false"
            ></lume-point-light>
            <lume-element3d id="body" size="0 0 0" rotation=${() => (this.still || this.bust ? '0 0 0' : swayRotation)}>
              <lume-element3d
                id="pendant"
                rotation=${() => `${this.tilt} 0 0`}
                position=${() => `0 ${PENDANT_TOP + this.height / 2} 0`}
              >
                ${() =>
                  this.shapes.map(
                    shape =>
                      html` <lume-shape
                        prop:shape=${shape}
                        size=${`1 1 ${DEPTH}`}
                        center-geometry="false"
                        bevel="true"
                        bevel-thickness="1.3"
                        bevel-segments="3"
                        curve-segments="6"
                        cast-shadow="true"
                        metalness="1"
                        attr:color=${() => matProps().color}
                        attr:roughness=${() => matProps().roughness}
                        position="0 0 0"
                        align-point="0.5 0.5 0.5"
                        mount-point="0.5 0.5 0.5"
                      ></lume-shape>`,
                  )}
              </lume-element3d>
            </lume-element3d>
          </lume-element3d>
        </lume-scene>
      `
    }
  },
)
